import { getFirestore, collection, getDocs } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { ContentService } from "../../services/content.service.js";

import { EpisodeList } from "../feature/series/EpisodeList.js";
// Investigating EpisodeList... reuse is better if possible, but might need modification to accept 'purchasedMap'
// For now, let's assume we can pass purchased status or handle it.

const db = getFirestore(app);

export const LibraryTab = async (target, user) => {
    // 1. Fetch Purchased/Rented Episodes
    // Group by Series ID
    const purchasedRef = collection(db, "users", user.uid, "purchased_episodes");
    const snapshot = await getDocs(purchasedRef);

    // [REFACTOR] Early return removed to allow merging with Read History
    // if (snapshot.empty) return; -> Moved check to below

    // 2. Data Processing
    const libraryMap = new Map(); // seriesId -> { seriesData, episodes: [] }
    const seriesIds = new Set();
    const purchasedMap = {}; // `${seriesId}_${episodeId}` -> { type, expiresAt }

    snapshot.docs.forEach(doc => {
        const data = doc.data();
        seriesIds.add(data.seriesId);

        // Populate purchasedMap for badge rendering
        purchasedMap[`${data.seriesId}_${data.episodeId}`] = {
            type: data.type, // 'rent' | 'own'
            expiresAt: data.expiresAt
        };
    });

    // [FIX] Source B: Read History (Subcollection)
    // We fetch the most recent 50 items to ensure "Recently Viewed" are included
    try {
        const { HistoryService } = await import("../../services/history.service.js");
        const historyResult = await HistoryService.getHistory(user.uid, 50);
        if (historyResult && historyResult.items) {
            historyResult.items.forEach(item => {
                if (item.seriesId) seriesIds.add(item.seriesId);
            });
        }
    } catch (e) {
        console.warn("[Library] Failed to fetch history:", e);
    }

    // [FIX] Delayed Empty Check
    if (seriesIds.size === 0) {
        target.innerHTML = `
            <div class="flex flex-col items-center justify-center py-20 text-center">
                <div class="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-300">
                    <span class="material-symbols-outlined text-4xl">local_library</span>
                </div>
                <h3 class="text-gray-900 font-bold mb-1">${window.locale.t('library.empty_title')}</h3>
                <p class="text-gray-500 text-sm mb-6">${window.locale.t('library.empty_desc')}</p>
                <button onclick="window.router.navigate('/')" class="px-5 py-2 bg-indigo-50 text-indigo-600 font-bold rounded-full text-sm hover:bg-indigo-100 transition-colors">${window.locale.t('library.explore_btn')}</button>
            </div>
        `;
        return;
    }

    // 3. Fetch Series Details (Batch)
    const sidArray = Array.from(seriesIds);
    let seriesList = [];

    if (sidArray.length > 0) {
        try {
            // [FIX] Use getSeriesByIds (plural) for batch fetch + cleanup
            // Assuming this service method exists and works
            seriesList = await ContentService.getSeriesByIds(sidArray);
        } catch (e) {
            console.warn("[Library] Batch fetch failed", e);
        }
    }

    // Layout
    target.innerHTML = `
        <div class="flex flex-col md:flex-row h-[600px] border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
            <!-- Left: Series List -->
            <div class="w-full md:w-1/3 border-b md:border-b-0 md:border-r border-gray-100 overflow-y-auto bg-gray-50/50" id="lib-series-list">
                <!-- Items injected here -->
            </div>

            <!-- Right: Episode Details -->
            <div class="w-full md:w-2/3 h-full overflow-y-auto relative hidden md:block" id="lib-detail-view">
                 <div class="flex flex-col items-center justify-center h-full text-gray-400">
                    <span class="material-symbols-outlined text-4xl mb-2 opacity-30">menu_book</span>
                    <p class="text-sm">${window.locale.t('library.select_hint')}</p>
                 </div>
            </div>
            <!-- Loading Overlay for Mobile/Desktop -->
            <div id="lib-loading" class="hidden absolute inset-0 bg-white/80 z-50 flex items-center justify-center">
                <span class="material-symbols-outlined animate-spin text-indigo-500">progress_activity</span>
            </div>
        </div>
    `;

    const seriesContainer = target.querySelector('#lib-series-list');
    const detailContainer = target.querySelector('#lib-detail-view');

    // Render Series List
    seriesList.forEach(series => {
        const el = document.createElement('div');
        el.className = "flex items-center gap-3 p-4 hover:bg-white cursor-pointer transition-colors border-b border-gray-100 last:border-0";

        const thumb = series.coverImage || series.thumbnail || 'https://placehold.co/50';
        const author = series.authorName || series.author || 'Unknown Author';

        el.innerHTML = `
            <img src="${thumb}" class="w-12 h-16 object-cover rounded shadow-sm bg-gray-200" loading="lazy" />
            <div class="flex-1 min-w-0">
                <h4 class="font-bold text-gray-900 text-sm truncate">${series.title || 'Untitled'}</h4>
                <p class="text-xs text-gray-500 truncate">${author}</p>
            </div>
            <span class="material-symbols-outlined text-gray-300 text-sm">chevron_right</span>
        `;
        el.onclick = () => selectSeries(series);
        seriesContainer.appendChild(el);
    });

    // Selection Logic
    const selectSeries = async (series) => {
        if (detailContainer.classList.contains('hidden')) detailContainer.classList.remove('hidden'); // Mobile Show
        detailContainer.innerHTML = '<div class="p-10 flex justify-center"><span class="material-symbols-outlined animate-spin text-gray-300">progress_activity</span></div>';

        try {
            const isAuthor = (user && user.uid === series.authorId);

            // 1. Fetch Published Episodes (or All if Author)
            let episodes = await ContentService.getEpisodes(series.id, isAuthor);

            // 2. [FIX] Merge Hidden Purchased/Read Episodes
            // Only perform if NOT author (Author already sees everything)
            if (!isAuthor) {
                const loadedIds = new Set(episodes.map(e => String(e.id).trim())); // Normalize
                const prefix = `${series.id}_`;
                const missingIdsToFetch = new Set();

                // (A) Check Purchasing Map
                if (purchasedMap) {
                    Object.keys(purchasedMap).forEach(key => {
                        if (key.startsWith(prefix)) {
                            const eid = key.slice(prefix.length);
                            if (!loadedIds.has(String(eid).trim())) {
                                missingIdsToFetch.add(eid);
                            }
                        }
                    });
                }

                // (B) Check Read History
                if (user.readHistory && user.readHistory[series.id]) {
                    const historyList = user.readHistory[series.id];
                    if (Array.isArray(historyList)) {
                        historyList.forEach(eid => {
                            if (!loadedIds.has(String(eid).trim())) {
                                missingIdsToFetch.add(eid);
                            }
                        });
                    }
                }

                // Fetch Missing
                const missingArray = Array.from(missingIdsToFetch);
                if (missingArray.length > 0) {
                    console.log("[Library] Fetching missing episodes (Purchased/Free):", missingArray);
                    const { doc, getDoc } = await import("firebase/firestore");
                    const promises = missingArray.map(async (eid) => {
                        try {
                            const snap = await getDoc(doc(db, "series", series.id, "episodes", eid));
                            if (snap.exists()) return { id: snap.id, ...snap.data() };
                            return null;
                        } catch (e) { return null; }
                    });
                    const fetched = (await Promise.all(promises)).filter(e => e !== null);
                    episodes = [...episodes, ...fetched];
                }
            }

            // 3. [CRITICAL] Strict Deduplication using Map + Trim
            const uniqueMap = new Map();
            episodes.forEach(ep => {
                // Key = String ID trimmed
                const key = String(ep.id).trim();
                uniqueMap.set(key, ep);
            });
            const uniqueEpisodes = Array.from(uniqueMap.values());

            // 4. [FIX v1.36.204] Secure Sort (Universal + Tie Breaker)
            // Supports Order/Index collisions by falling back to Creation Time.
            uniqueEpisodes.sort((a, b) => {
                // Get Sort Key: Order > Index > CreatedAt
                const keyA = (a.order !== undefined) ? a.order : (a.index !== undefined ? a.index : null);
                const keyB = (b.order !== undefined) ? b.order : (b.index !== undefined ? b.index : null);

                if (keyA !== null && keyB !== null) {
                    const diff = Number(keyA) - Number(keyB);
                    if (diff !== 0) return diff; // Primary Sort
                }

                // Tie-Breaker: CreatedAt (Ascending)
                // Ensures duplicates (e.g. Order 4, 4) sort by who was created first (likely correct sequence)
                const ca = a.createdAt?.seconds || 0;
                const cb = b.createdAt?.seconds || 0;
                return ca - cb;
            });

            // 5. [FIX] Calculate Free Status (Global Policy)
            try {
                const globalSettings = await ContentService.getGlobalSettings();
                // Robust Fallback: Handle Null / Empty String / Undefined
                const val = series.freeLimit;
                const freeLimit = (val !== undefined && val !== null && val !== "")
                    ? Number(val)
                    : (globalSettings.default_free_limit || 3);

                // Inject 'isFree' status based on VISUAL INDEX
                uniqueEpisodes.forEach((ep, idx) => {
                    // Logic: 1st, 2nd, 3rd items are Free if limit is 3.
                    if ((idx + 1) <= freeLimit) {
                        ep.isFree = true;
                    }
                });
            } catch (err) {
                console.warn("[Library] Failed to calc free limit:", err);
            }

            renderDetail(series, uniqueEpisodes);
        } catch (e) {
            console.error(e);
            detailContainer.innerHTML = `<div class="p-8 text-red-500 text-center">로드 실패: ${e.message}</div>`;
        }
    };

    const renderDetail = (series, episodes) => {
        detailContainer.innerHTML = `
            <div class="sticky top-0 bg-white/90 backdrop-blur z-10 p-4 border-b border-gray-100 flex justify-between items-center">
                <div>
                     <h3 class="font-bold text-lg text-gray-900">${series.title}</h3>
                     <p class="text-xs text-gray-500">${window.locale.t('library.total_episodes').replace('{n}', episodes.length)}</p>
                </div>
                <button class="md:hidden p-2 text-gray-500" onclick="document.getElementById('lib-detail-view').classList.add('hidden')">
                     <span class="material-symbols-outlined">close</span>
                </button>
            </div>
            <div class="p-4 space-y-2">
                ${episodes.map((ep, idx) => {
            // Check Status
            const purchaseInfo = purchasedMap[`${series.id}_${ep.id}`];
            let badge = '';

            // [FIX] Badge Logic Priority: Ordered > Rented > Free > None
            if (purchaseInfo) {
                const { type, expiresAt } = purchaseInfo;
                if (type === 'own') {
                    badge = '<span class="px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-bold rounded flex items-center gap-1 border border-purple-200"><span class="material-symbols-outlined text-[10px]">check_circle</span>소장</span>';
                } else if (type === 'rent') {
                    const now = new Date();
                    const exp = expiresAt?.toDate ? expiresAt.toDate() : new Date(expiresAt);
                    const diff = exp - now;
                    if (diff > 0) {
                        const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
                        badge = `<span class="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded border border-green-200">대여 D-${days}</span>`;
                    } else {
                        badge = `<span class="px-2 py-0.5 bg-gray-100 text-gray-500 text-[10px] font-bold rounded border border-gray-200">만료됨</span>`;
                    }
                }
            } else if (ep.isFree) {
                // [NEW] Free Badge
                badge = '<span class="px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded border border-blue-100">무료</span>';
            }

            // [FIX v1.36.205] Thumbnail Fallback Logic
            // Priority: Episode Specific (thumbnail/thumb/formatted) > Series Cover > Default Placeholder
            const epThumb = ep.thumbnail || ep.thumb || ep.formattedThumbnail || series.thumbnail || series.coverImage;

            return `
                        <div class="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group border border-transparent hover:border-indigo-100" onclick="window.router.navigate('/series/${series.id}/episode/${ep.id}')">
                            <div class="flex items-center gap-3 flex-1 overflow-hidden">
                                <!-- [FIX] Use Index + 1 for display order to prevent "4, 4, 4" data issues -->
                                <span class="text-xs font-mono text-gray-400 w-6 text-center shrink-0 font-bold group-hover:text-indigo-500">${idx + 1}</span>
                                
                                <div class="w-12 h-12 bg-gray-200 rounded overflow-hidden shrink-0 border border-gray-100 relative">
                                    ${epThumb
                    ? `<img src="${epThumb}" class="w-full h-full object-cover" loading="lazy">`
                    : `<div class="w-full h-full flex items-center justify-center text-gray-400"><span class="material-symbols-outlined text-sm">image</span></div>`
                }
                                </div>

                                <div class="min-w-0 flex-1 pr-2">
                                    <h5 class="text-sm font-medium text-gray-800 group-hover:text-indigo-600 transition-colors truncate">
                                        ${ep.title || 'Untitled'}
                                    </h5>
                                    <div class="flex items-center gap-2 mt-0.5">
                                        <span class="text-[10px] text-gray-400 truncate">${ep.updatedAt?.toDate ? ep.updatedAt.toDate().toLocaleDateString() : ''}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="shrink-0 pl-2 text-right min-w-[60px] flex justify-end">
                                ${badge}
                            </div>
                        </div>
                    `;
        }).join('')}
            </div>
        `;
    };
};
